// The ticket workspace's vocabulary: a Jira ticket's runs are its attempts, one
// is selected (by job id, never by position), and one of seven views shows it.
// Pure functions, shared by the workspace, the sidebar, Mission Control and the
// palette so every entry point opens the same context.
import type { RunRecord } from "@/api/types";
import { flowLabel } from "@/lib/format";

export const TICKET_VIEWS = ["overview", "agents", "map", "changes", "impact", "activity", "context"] as const;
export type TicketView = (typeof TICKET_VIEWS)[number];

export const VIEW_LABEL: Record<TicketView, string> = {
  overview: "Overview", agents: "Agents", map: "Code map", changes: "Changes", impact: "Impact", activity: "Activity", context: "Context",
};

/** What each view covers. Code map spans every attempt; the rest follow the selected one. */
export const VIEW_SCOPE: Record<TicketView, string> = {
  overview: "What needs a decision: the request, the selected attempt's status, all attempts and the ticket's lineage.",
  agents: "Who did what in the selected attempt: its agents, their messages and their state.",
  map: "Where the ticket touches the code, across all attempts.",
  changes: "What the selected attempt changed and what verified it.",
  impact: "What the selected attempt's change could reach: dependents, routes, events and resources. Reach is not breakage.",
  activity: "The selected attempt's events in order: steps, tools, decisions, denials and errors.",
  context: "What the selected attempt touched in Jira, knowledge, code and GitHub, why, and what still needs a person.",
};

export function isTicketView(v: unknown): v is TicketView {
  return typeof v === "string" && (TICKET_VIEWS as readonly string[]).includes(v);
}

export const TICKET_KEY_RE = /^[A-Z][A-Z0-9_]{0,19}-\d+$/;

export type AttemptKind = "fix" | "analyze" | "fullstack" | "iterate" | "compare" | "codex" | "other";

const ANALYZE = "f/jira_bug_analysis_deep/main";

export function attemptKind(r: Pick<RunRecord, "flow">): AttemptKind {
  switch (r.flow) {
    case "f/jira_bug_analysis_fix/main": return "fix";
    case ANALYZE: return "analyze";
    case "f/jira_bug_analysis_fix_fullstack/main": return "fullstack";
    case "f/jira_bug_analysis_iterate/main": return "iterate";
    case "f/jira_bug_analysis_fix_compare/main": return "compare";
    case "f/jira_bug_analysis_fix_codex/main": return "codex";
    default: return "other";
  }
}

const KIND_LABEL: Record<AttemptKind, string> = {
  fix: "Fix", analyze: "Analyze", fullstack: "Fullstack fix",
  iterate: "Iterate", compare: "Compare", codex: "Fix · Codex", other: "",
};

type GateFields = "status" | "awaiting_approval_at" | "approved_by" | "rejected_by" | "plan_outcome";

/** An attempt that stopped at its plan: waiting for a decision, rejected, or expired. Nothing was implemented. */
export function stoppedAtPlan(r: Pick<RunRecord, GateFields>): boolean {
  return planPending(r) || r.plan_outcome === "rejected" || r.plan_outcome === "expired";
}

/** Analysis-only attempts, and attempts that stopped at their plan, have no patch. */
export function writesCode(r: Pick<RunRecord, "flow" | GateFields>): boolean {
  return r.flow !== ANALYZE && !stoppedAtPlan(r);
}

export interface Attempt {
  run: RunRecord;
  n: number;            // 1-based, oldest first: what people say ("attempt 2")
  number: string;       // "02"
  kind: AttemptKind;
  label: string;        // "02 · Plan", "03 · Fix · A/B fable-plan"
}

/** A ticket's runs as attempts, oldest first. */
export function attemptsOf(runs: RunRecord[]): Attempt[] {
  return [...runs]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.job_id.localeCompare(b.job_id))
    .map((run, i) => {
      const kind = attemptKind(run);
      const number = String(i + 1).padStart(2, "0");
      const what = KIND_LABEL[kind] || flowLabel(run.flow);
      return { run, n: i + 1, number, kind, label: [number, what, run.ab_label ? `A/B ${run.ab_label}` : ""].filter(Boolean).join(" · ") };
    });
}

/** A running attempt whose plan waits for a person to approve or reject it. */
export function planPending(r: Pick<RunRecord, GateFields>): boolean {
  return r.status === "running" && !!r.awaiting_approval_at && !r.approved_by && !r.rejected_by;
}

/** The attempt a ticket opens on: a plan waiting for a decision, else a running one, else the newest. */
export function defaultAttempt(runs: RunRecord[]): RunRecord | null {
  if (!runs.length) return null;
  const newest = [...runs].sort((a, b) => b.created_at.localeCompare(a.created_at));
  return newest.find(planPending) ?? newest.find((r) => r.status === "running") ?? newest[0];
}

/** The view a ticket opens on when the link doesn't name one: a plan to decide on opens on it. */
export function defaultView(r: RunRecord | null): TicketView {
  if (r && planPending(r)) return "overview";
  if (r?.status === "running") return "agents";
  return "overview";
}

export interface TicketSummary {
  key: string;
  title: string;
  runs: RunRecord[];
  latest: RunRecord;
  active: number;
  pendingPlans: number;
  prs: number;
  failedLatest: boolean;
}

/** Runs grouped by Jira ticket, the most recently active ticket first. */
export function ticketsFrom(runs: RunRecord[]): TicketSummary[] {
  const by = new Map<string, RunRecord[]>();
  for (const r of runs) {
    const key = r.jira_ticket;
    if (!key) continue;
    (by.get(key) ?? by.set(key, []).get(key)!).push(r);
  }
  return [...by.entries()].map(([key, rs]) => {
    const sorted = [...rs].sort((a, b) => b.created_at.localeCompare(a.created_at));
    return {
      key, runs: sorted, latest: sorted[0], title: sorted.find((r) => r.title && r.title !== key)?.title ?? "",
      active: rs.filter((r) => r.status === "running").length,
      pendingPlans: rs.filter(planPending).length,
      prs: rs.filter((r) => r.pr_url).length,
      failedLatest: sorted[0].status === "failure",
    };
  }).sort((a, b) => b.latest.created_at.localeCompare(a.latest.created_at));
}

/** The canonical link to a ticket's view of one attempt. */
export function ticketLink(key: string, opts: { view?: TicketView; run?: string } = {}): { path: string; query: Record<string, string> } {
  const query: Record<string, string> = {};
  if (opts.view) query.view = opts.view;
  if (opts.run) query.run = opts.run;
  return { path: `/tickets/${encodeURIComponent(key)}`, query };
}

/** Where a run lives: its ticket's workspace, or the standalone run page for flows without a ticket. */
export function runLink(r: Pick<RunRecord, "job_id" | "jira_ticket" | "status">, view?: TicketView) {
  if (r.jira_ticket && TICKET_KEY_RE.test(r.jira_ticket)) {
    return ticketLink(r.jira_ticket, { run: r.job_id, view: view ?? (r.status === "running" ? "agents" : undefined) });
  }
  return { path: `/runs/${r.job_id}`, query: {} as Record<string, string> };
}

export type NextKind = "loading" | "queued" | "planning" | "working" | "plan_pending" | "plan_rejected" | "plan_expired"
  | "duplicate" | "review_pr" | "dry_run" | "findings" | "no_change" | "stopped" | "failed" | "finished";

export interface NextAction {
  kind: NextKind;
  title: string;
  body: string;
  primary?: { label: string; view?: TicketView; href?: string; run?: string; anchor?: string };
  secondary?: { label: string; view?: TicketView; launch?: boolean };
}

interface JobLike {
  running: boolean;
  success?: boolean;
  agent_step?: string | null;
  awaiting_approval?: boolean;
  started_at?: string | null;
  steps?: { id: string; status: string }[];
  error?: { step_id?: string; message?: string; name?: string } | null;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

const truthy = (v: unknown) => v === true || v === "True" || v === "true";

/** What the selected attempt needs from a person next, from its real state only. */
export function nextAction(r: RunRecord, job: JobLike | null): NextAction {
  if (!job) return { kind: "loading", title: "Reading the attempt…", body: "Its steps, events and results load from the run." };
  const res = job.result ?? {};
  if (job.running) {
    if (planPending(r) || (job.awaiting_approval && !r.approved_by && !r.rejected_by)) {
      return { kind: "plan_pending", title: "Decide on the plan.", body: "Read the root cause, the files and the risks. Approving lets this attempt implement it; nothing is written to GitHub before then.",
        primary: { label: "Review the plan", anchor: "plan" }, secondary: { label: "Proposed reach", view: "impact" } };
    }
    const started = (job.steps ?? []).some((s) => s.status !== "waiting");
    if (!started) {
      return { kind: "queued", title: "Starting.", body: "The run is preparing: the clone, the ticket and what earlier runs learned. No agent is working on it yet.",
        primary: { label: "Activity", view: "activity" } };
    }
    if (job.agent_step === "plan_analyze") {
      return { kind: "planning", title: "The planner is reading the code.", body: "A read-only planner investigates and proposes a plan. Nothing is written until a person approves it.",
        primary: { label: "See the agent team", view: "agents" }, secondary: { label: "Activity", view: "activity" } };
    }
    return { kind: "working", title: "The agents are working.", body: "Follow who is doing what, and the files changing so far.",
      primary: { label: "See the agent team", view: "agents" }, secondary: { label: "Changes so far", view: "changes" } };
  }
  if (job.success === false) {
    if (r.canceled_by) {
      return { kind: "stopped", title: "This attempt was stopped.", body: `Stopped by ${r.canceled_by}. What it wrote before the stop is kept in its activity and artifacts.`,
        primary: { label: "Activity", view: "activity" }, secondary: { label: "New attempt", launch: true } };
    }
    const where = job.error?.step_id ?? r.error_step;
    return { kind: "failed", title: "Find out why it failed.", body: where ? `It stopped at “${where}”. The activity shows the last events and the error.` : "The activity shows the last events and the error.",
      primary: { label: "Inspect the failure", view: "activity" }, secondary: { label: "New attempt", launch: true } };
  }
  if (r.plan_outcome === "rejected") {
    return { kind: "plan_rejected", title: "The plan was rejected.", body: `${r.rejected_by ?? "Someone"} rejected it${r.rejected_reason ? `: “${r.rejected_reason.trim().replace(/[.\s]+$/, "")}”` : ""}. Nothing was implemented; a new attempt plans again.`,
      primary: { label: "Read the plan", anchor: "plan" }, secondary: { label: "New attempt", launch: true } };
  }
  if (r.plan_outcome === "expired") {
    return { kind: "plan_expired", title: "The plan expired.", body: "No one decided on it within a week, so nothing was implemented. A new attempt plans again.",
      primary: { label: "Read the plan", anchor: "plan" }, secondary: { label: "New attempt", launch: true } };
  }
  if (r.deduped) {
    return { kind: "duplicate", title: "Another attempt was already running.", body: "A run of this ticket and repository had started minutes before, so this one stopped before doing anything.",
      secondary: { label: "New attempt", launch: true } };
  }
  const pr = (res.pr_url as string | undefined) || r.pr_url;
  if (pr) {
    return { kind: "review_pr", title: "Review the pull request.", body: "The agents' own review is evidence for the PR, not permission to merge it.",
      primary: { label: "Open the PR", href: pr }, secondary: { label: "Changes & tests", view: "changes" } };
  }
  if (truthy(job.args?.dry_run) || truthy(res.dry_run)) {
    return { kind: "dry_run", title: "Read the dry run.", body: "It ran for real but wrote nothing to GitHub: no issue, branch, commit or PR.",
      primary: { label: "Changes & tests", view: "changes" } };
  }
  if (r.flow === "f/jira_bug_analysis_deep/main") {
    return { kind: "findings", title: "Read the findings.", body: "An analysis attempt investigates and explains; it changes no code.",
      primary: { label: "Findings", view: "changes" } };
  }
  if (res.fix_applied === false || r.fix_applied === false) {
    return { kind: "no_change", title: "No change was made.", body: String(res.skipped_reason || "The agents judged that no code change was needed. Their reasoning is in the findings."),
      primary: { label: "Findings", view: "changes" } };
  }
  return { kind: "finished", title: "The attempt finished.", body: "See what changed and what verified it.", primary: { label: "Changes & tests", view: "changes" } };
}

const DEFAULT_JIRA = "https://foodics-products.atlassian.net";

/** A ticket's Jira page. `base` comes from a run's arguments, which any member
 * can set, so only an http(s) address is used; anything else (`javascript:`)
 * falls back to the workspace's Jira. */
export function jiraBrowseUrl(base: unknown, key: string): string {
  const b = typeof base === "string" && /^https?:\/\/[^\s"'<>]+$/i.test(base.trim()) ? base.trim().replace(/\/$/, "") : DEFAULT_JIRA;
  return `${b}/browse/${encodeURIComponent(key)}`;
}

/** `url` when it is an http(s) address, else null. */
export function httpUrl(url: unknown): string | null {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}
